// All functions are derived from cable_stage_information in legacy
import { computeCableLabel } from "@/models/equipment/cables/properties";
import { computeMleLabel } from "@/models/equipment/mle/properties";
import type { Cables, Motor_Lead_Extensions } from "gql/api";
import { SURFACE_CABLE } from "./constants";

// https://github.com/SummitESP/summit-sizing/blob/master/client/app/js/calclib_calculations.js#L5179
function getCableLengthKF(cableLengthFt: number): number {
  return cableLengthFt / 1000;
}
export function getCableReactance(
  averageCableTemperatureF: number,
  cableLengthFt: number,
  frequency: number,
  cableResistance: number,
  cableReactance: number
): number {
  const tempRCorr = averageCableTemperatureF * 0.0021 + 0.8364;
  const cableLengthKF = getCableLengthKF(cableLengthFt);
  const freqRatio = frequency / 60;

  const rTempLength = cableLengthKF * cableResistance * tempRCorr;
  const xTempLength = cableReactance * cableLengthKF * freqRatio;
  return Math.sqrt(rTempLength * rTempLength + xTempLength * xTempLength);
}

export function calculateMleEllipseMaxDepth(
  ellipseAngle: number,
  depth: number,
  outerDiameterIn: number,
  machineIndentIn: number,
  isMotor = true
): number {
  // SEC(x) = 1/COS(x)
  const secant = 1 / Math.cos(ellipseAngle);

  // Formula: SEC(ellipseAngle) * (width + depth) - depth
  if (isMotor) {
    return (
      secant * (depth + outerDiameterIn - machineIndentIn) -
      outerDiameterIn -
      machineIndentIn
    );
  }
  return secant * (depth + outerDiameterIn) - outerDiameterIn;
}

export function calculateEllipseMaxDepth(width: number, depth: number): number {
  const ellipseAngle = Math.atan(depth / width);
  const maxDepth = depth * Math.sin(ellipseAngle);

  return maxDepth;
}

/**
 * Tubing + Cable OD (inches) — ellipse formula for cable on tubing coupling.
 * Same value shown as "Tubing + Cable OD" in the cross-section chart.
 * Returns tubingCouplingODIn when inputs are missing/zero to avoid Infinity/NaN.
 */
export function getTubingCableODIn(
  tubingCouplingODIn: number,
  cableWidthIn: number,
  cableDepthIn: number
): number {
  const denominator = tubingCouplingODIn + cableDepthIn;
  if (denominator <= 0 || !Number.isFinite(denominator)) {
    return tubingCouplingODIn;
  }
  const angleRad = Math.atan(cableWidthIn / 2 / denominator);
  const cosAngle = Math.cos(angleRad);
  if (cosAngle <= 0 || !Number.isFinite(cosAngle)) {
    return tubingCouplingODIn;
  }
  const ellipseDepthIn =
    (1 / cosAngle) * (cableDepthIn + tubingCouplingODIn) - tubingCouplingODIn;
  const result = ellipseDepthIn + tubingCouplingODIn;
  return Number.isFinite(result) && result > 0 ? result : tubingCouplingODIn;
}

// Cable on surface calculation (math.max bc non negative number)
export function calculateCableOnSurface(
  totalCablesLengthFt: number,
  downholeDepthFt: number
): number {
  return Math.max(0, totalCablesLengthFt - downholeDepthFt);
}

export function getCableVoltLossV(
  cableImpedance: number,
  operatingCurrent: number
): number {
  return cableImpedance * operatingCurrent * 1.732;
}

export function getCableVoltLossVPerThousandFTV(
  voltLossV: number,
  cableLengthFt: number
): number {
  const cableLengthKF = getCableLengthKF(cableLengthFt);
  return voltLossV / cableLengthKF;
}

export function getCableKWLoss(
  cableLengthFT: number,
  cableResistance: number,
  averageCableTemperatureF: number,
  operatingCurrent: number
): number {
  const cableResistanceCalc =
    ((cableLengthFT * cableResistance) / 1000) *
    (1 + 0.00214 * (averageCableTemperatureF - 77));

  return (operatingCurrent * operatingCurrent * cableResistanceCalc * 3) / 1000;
}

export const getTemperatureDeltaForCurrent = (
  cableAmpFactor: number,
  operatingCurrent: number
): number => cableAmpFactor * Math.pow(operatingCurrent, 2);

export function getCableMaxTemperatureF(
  cableAmpFactor: number,
  operatingCurrent: number,
  temperatureCableEndF: number
): number {
  return (
    getTemperatureDeltaForCurrent(cableAmpFactor, operatingCurrent) +
    temperatureCableEndF
  );
}

export const getMaxCurrentAtTemperatureA = (
  cableMaxTemperatureRatingF: number,
  temperatureCableEndF: number,
  cableAmpFactor: number
): number =>
  temperatureCableEndF > cableMaxTemperatureRatingF
    ? 0 // over max heat, 0 amps, don't take sqrt of negative number
    : Math.sqrt(
        (cableMaxTemperatureRatingF - temperatureCableEndF) / cableAmpFactor
      );

export function getTemperatureAtCableLengthDownholeF(
  downholeCableLengthFt: number,
  tempGradFFt: number,
  surfaceTemperatureF: number
): number {
  return downholeCableLengthFt * tempGradFFt + surfaceTemperatureF;
}

export function getAverageTemperatureOfDownholeCableF(
  cableStartTempF: number,
  cableEndTempF: number
): number {
  return (cableStartTempF + cableEndTempF) / 2;
}

export function isSurfaceCable(id: string): boolean {
  return id === SURFACE_CABLE;
}

export type CableData = {
  cableHeader: string;
  cableLengthFT: number;
  averageAmbientTemperatureF: number;
  cableMaxTemperatureF: number;
  maxAmpsAtMaxTemperatureA: number;
  voltLossPerThousandFTV: number;
  totalVoltLossV: number;
  totalVoltLossAtNameplateV: number;
  cableMotorLoadRatio: number;
  powerLossKW: number;
  cableReactance: number;
  cablePowerCostPerMonthUSD: number;
  downholeTempAtCableStartF: number;
  downholeTempAtCableEndF: number;
  cableWeightLbs: number;
  mleStartMD?: number;
  mleEndMD?: number;
  mleEllipseMaxDepth?: number;
};

export const getCableData = (
  cable: Partial<Cables | Motor_Lead_Extensions> | null | undefined,
  cableLength: number,
  pumpHz: number,
  motorCurrent: number,
  downholeTempAtCableStartF: number,
  downholeTempAtCableEndF: number,
  cableCostkWH: number,
  reratedNameplateA: number,
  isSurfaceCable?: boolean
): CableData => {
  const data: CableData = {
    cableHeader: "",
    cableLengthFT: 0,
    averageAmbientTemperatureF: 0,
    cableMaxTemperatureF: 0,
    maxAmpsAtMaxTemperatureA: 0,
    voltLossPerThousandFTV: 0,
    totalVoltLossV: 0,
    totalVoltLossAtNameplateV: 0,
    powerLossKW: 0,
    cableReactance: 0,
    cableMotorLoadRatio: 0,
    cablePowerCostPerMonthUSD: 0,
    downholeTempAtCableStartF,
    downholeTempAtCableEndF,
    cableWeightLbs: 0
  };

  if (!cable) {
    return data;
  }

  data.averageAmbientTemperatureF = getAverageTemperatureOfDownholeCableF(
    downholeTempAtCableStartF,
    downholeTempAtCableEndF
  );

  if (cable?.__typename === "motor_lead_extensions") {
    data.cableHeader = computeMleLabel(cable);
  } else {
    data.cableHeader = computeCableLabel(cable as Cables, isSurfaceCable);
  }

  data.cableLengthFT = cableLength;

  data.cableMaxTemperatureF = getCableMaxTemperatureF(
    cable.amp_factor,
    motorCurrent,
    downholeTempAtCableEndF
  );

  const cableTempF =
    downholeTempAtCableEndF +
    getTemperatureDeltaForCurrent(cable.amp_factor, motorCurrent);

  data.maxAmpsAtMaxTemperatureA = getMaxCurrentAtTemperatureA(
    cable.max_temperature_fahrenheit || 0,
    cableTempF,
    cable.amp_factor
  );

  data.cableReactance = getCableReactance(
    data.averageAmbientTemperatureF,
    cableLength,
    pumpHz,
    cable.resistance_per_1000_ft,
    cable.impedance_60_hz_per_1000_ft
  );

  data.totalVoltLossV = getCableVoltLossV(data.cableReactance, motorCurrent);
  data.totalVoltLossAtNameplateV = getCableVoltLossV(
    data.cableReactance,
    reratedNameplateA
  );

  data.voltLossPerThousandFTV = getCableVoltLossVPerThousandFTV(
    data.totalVoltLossV,
    cableLength
  );

  data.powerLossKW = getCableKWLoss(
    cableLength,
    cable.resistance_per_1000_ft,
    data.averageAmbientTemperatureF,
    motorCurrent
  );

  if (data.maxAmpsAtMaxTemperatureA !== 0) {
    data.cableMotorLoadRatio = motorCurrent / data.maxAmpsAtMaxTemperatureA;
  }

  data.cablePowerCostPerMonthUSD = data.powerLossKW * 24 * 30 * cableCostkWH;

  data.cableWeightLbs = cableLength * cable.pound_per_foot;

  return data;
};
